import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination
} from '@mui/material';
import { Receipt, Search, Refresh, CheckCircle } from '@mui/icons-material';
import useInvoice from '../hooks/useInvoice';
import AdminPageHeader from '../components/AdminPageHeader';

// Helper para obtener fecha local
const getLocalDateString = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const Invoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 1
  });
  const [filters, setFilters] = useState(() => {
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    return {
      page: 0, // MUI usa índice 0
      limit: 20,
      startDate: getLocalDateString(firstDayOfMonth),
      endDate: getLocalDateString(today)
    };
  });

  const { loading, error, listInvoices } = useInvoice();

  useEffect(() => {
    loadInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.page, filters.limit]);

  const loadInvoices = async () => {
    const params = {
      ...filters,
      page: filters.page + 1 // Backend usa índice 1
    };
    const result = await listInvoices(params);
    if (result.success) {
      setInvoices(result.data);
      setPagination(result.pagination);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setFilters(prev => ({ ...prev, page: 0 }));
    loadInvoices();
  };

  const handleChangePage = (event, newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  const handleChangeRowsPerPage = (event) => {
    setFilters(prev => ({
      ...prev,
      limit: parseInt(event.target.value, 10),
      page: 0
    }));
  };

  const handleRefresh = () => {
    loadInvoices();
  };

  return (
    <Box className="admin-page">
      <AdminPageHeader
        eyebrow="COMPROBANTES"
        title="Facturas emitidas"
        subtitle="Buscá y revisá los comprobantes generados por el sistema."
        icon={<Receipt />}
        action={<Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={handleRefresh}
          disabled={loading}
        >
          Actualizar
        </Button>}
      />

      {/* Filtros */}
      <Card className="mb-lg">
        <CardContent>
          <form onSubmit={handleSearch}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="date"
                  label="Fecha Desde"
                  name="startDate"
                  value={filters.startDate}
                  onChange={handleFilterChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="date"
                  label="Fecha Hasta"
                  name="endDate"
                  value={filters.endDate}
                  onChange={handleFilterChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  startIcon={<Search />}
                  disabled={loading}
                  size="large"
                >
                  Buscar
                </Button>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>

      {/* Error */}
      {error && (
        <Alert severity="error" className="mb-md">
          {error}
        </Alert>
      )}

      {/* Tabla */}
      {loading && !invoices.length ? (
        <Box className="d-flex justify-center align-center py-lg">
          <CircularProgress />
        </Box>
      ) : (
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Venta</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Número Factura</TableCell>
                  <TableCell>CAE</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell>Usuario</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body2" color="text.secondary" className="py-lg">
                        No se encontraron facturas en el período seleccionado
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  invoices.map((invoice) => (
                    <TableRow key={invoice.id} hover>
                      <TableCell>
                        {new Date(invoice.saleDate).toLocaleDateString('es-AR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric'
                        })}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="600">
                          {invoice.saleNumber}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={invoice.invoiceType}
                          size="small"
                          color={invoice.invoiceType.includes('B') ? 'primary' : 'secondary'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: 'monospace',
                            fontWeight: 600
                          }}
                        >
                          {invoice.invoiceNumber}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box className="d-flex align-center gap-xs">
                          <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: 'monospace',
                              fontSize: '0.75rem'
                            }}
                          >
                            {invoice.invoiceCAE}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="600">
                          ${parseFloat(invoice.total).toLocaleString('es-AR', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {invoice.user?.fullName || invoice.user?.username || '-'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Paginación */}
          {invoices.length > 0 && (
            <TablePagination
              component="div"
              count={pagination.total}
              page={filters.page}
              onPageChange={handleChangePage}
              rowsPerPage={filters.limit}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 20, 50, 100]}
              labelRowsPerPage="Filas por página:"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
              }
            />
          )}
        </Card>
      )}

      {/* Resumen */}
      {invoices.length > 0 && (
        <Box className="mt-md">
          <Typography variant="body2" color="text.secondary">
            Mostrando {invoices.length} de {pagination.total} facturas emitidas
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default Invoices;

